import type { ImageScenario } from '../game/imageTypes';

/**
 * Level 5 -- an active sprint board.
 *
 * Regions are measured by tools/find_arrows.py. The classification was
 * confirmed by the author on 2026-08-26:
 *
 *   - All three column-header arrows (1 New, 2 Refinement, 3 QA Testing) are
 *     one finding: the workflow has more statuses than the team is using.
 *   - Arrow 9, the hexagonal team avatar, is a DECOY. Assigning to a team is
 *     deliberate here; only the genuinely empty avatar on arrow 10 is the
 *     ownership mistake.
 *
 * Image is 2000 x 637. All regions are percentages of those dimensions.
 */
export const sprintBoard: ImageScenario = {
  id: 'jira-sprint-board',
  title: 'A sprint nobody can finish',
  subtitle: 'Active sprint board · JCS',

  brief:
    'A sprint board mid-flight, six columns wide. Ten arrows point at things somebody thought were worth a second look. Some are real process problems that will cost this team the sprint. The rest are the board working exactly as intended.',

  source: {
    toolId: 'jira',
    screen: 'Board › Active sprints',
    kind: 'screenshot',
    note: 'Captured from a ReleaseTEAM test instance, annotated with red arrows.',
  },

  image: {
    src: '/scenarios/sprint-board.webp',
    width: 2000,
    height: 637,
    alt: 'A Jira active sprint board with six columns: New, Refinement, To Do, Blocked, QA Testing and Done. Cards show story point badges, priority icons, assignee avatars and one red flag.',
  },

  arrows: [
    {
      id: 'column-new',
      label: '1',
      region: { x: 1.4, y: 12.0, w: 3.5, h: 16.0 },
      description: 'Arrow pointing down at the "New" column header',
    },
    {
      id: 'column-refinement',
      label: '2',
      region: { x: 17.0, y: 12.5, w: 6.0, h: 16.0 },
      description: 'Arrow pointing at the "Refinement" column header',
    },
    {
      id: 'column-qa-testing',
      label: '3',
      region: { x: 65.5, y: 14.0, w: 7.0, h: 14.5 },
      description: 'Arrow pointing down at the "QA Testing" column header',
    },
    {
      id: 'oversized-estimate',
      label: '4',
      region: { x: 24.0, y: 38.5, w: 7.0, h: 6.0 },
      description: 'Arrow pointing at the 100 point estimate on JCS-44',
    },
    {
      id: 'blocked-not-flagged',
      label: '5',
      region: { x: 57.5, y: 38.5, w: 6.5, h: 12.0 },
      description: 'Arrow pointing at JCS-20 in the Blocked column, which carries no flag',
    },
    {
      id: 'flagged-in-qa',
      label: '6',
      region: { x: 75.0, y: 41.0, w: 5.5, h: 12.5 },
      description: 'Arrow pointing at the red flag on JCS-48 in QA Testing',
      decoyNote:
        'This is the flag doing its job. JCS-48 is genuinely impeded, and it says so with a flag while staying in the column that reflects the work actually done on it. Compare it with the card sitting in the Blocked column with no flag at all: this is the pattern that one should be following.',
    },
    {
      id: 'done-low-priority',
      label: '7',
      region: { x: 92.0, y: 39.0, w: 6.0, h: 10.5 },
      description: 'Arrow pointing at the priority icon on JCS-10 in the Done column',
      decoyNote:
        'A blue chevron is a low priority, and this card is already in Done. Low priority work getting finished is not a problem — it is what a healthy sprint looks like when the important things were done first.',
    },
    {
      id: 'no-estimate',
      label: '8',
      region: { x: 26.5, y: 53.5, w: 6.5, h: 10.5 },
      description: 'Arrow pointing at the footer of JCS-39, where an estimate would be',
    },
    {
      id: 'team-owner',
      label: '9',
      region: { x: 45.5, y: 53.5, w: 6.0, h: 12.0 },
      description: 'Arrow pointing at the hexagonal team avatar on JCS-26',
      decoyNote:
        'A hexagonal avatar is a team rather than a person, and that is a deliberate choice here, not an empty field. Somebody has said who is accountable — it just happens to be a group. Compare it with the card whose avatar slot is genuinely empty: an owner you disagree with is a conversation, an owner nobody set is the actual problem.',
    },
    {
      id: 'unassigned-in-sprint',
      label: '10',
      region: { x: 13.0, y: 85.5, w: 5.5, h: 12.0 },
      description: 'Arrow pointing at the empty assignee avatar on JCS-19',
    },
  ],

  defects: [
    {
      id: 'too-many-statuses-new',
      arrowId: 'column-new',
      title: 'Three columns that all mean "not started"',
      severity: 'major',
      explanation:
        'New, Refinement and To Do are three separate columns for work that has not been started. Nobody can tell you the rule for moving a card from one to the next, which means the distinction is decoration — and a board where a column change carries no information is a board people stop updating.',
      fix: 'Collapse the pre-start columns into one To Do. If refinement genuinely is a stage with an entry and exit rule, keep exactly one column for it and write the rule down.',
    },
    {
      id: 'too-many-statuses-refinement',
      arrowId: 'column-refinement',
      title: 'Refinement is being tracked as sprint work',
      severity: 'major',
      explanation:
        'Refinement is preparation for a future sprint, not delivery of this one. Giving it a column inside the active sprint means the board is measuring two different things at once, and the sprint burndown counts work that was never part of the commitment.',
      fix: 'Refine in the backlog, not on the sprint board. If refinement must be visible, track it on a separate board rather than mixing it into the sprint the team is being measured on.',
    },
    {
      id: 'too-many-statuses-qa',
      arrowId: 'column-qa-testing',
      title: 'Six columns is more workflow than this team is using',
      severity: 'minor',
      explanation:
        'Six columns for a sprint this size means most of them hold nothing. QA Testing has one card, Done has one, and three columns share the not-started work. Every extra status is another decision a person has to make about where a card belongs, and the payoff has to justify that.',
      fix: 'Keep a status only if someone can state what has to be true for a card to enter and leave it. Merge the rest.',
    },
    {
      id: 'oversized-story',
      arrowId: 'oversized-estimate',
      title: 'A 100 point story inside a sprint',
      severity: 'critical',
      explanation:
        'An estimate this size is not an estimate, it is an admission that nobody knows. A story that large cannot be finished within the sprint, so it will roll over, and while it does it hides all of its own progress — the board shows one card not moving rather than the six pieces of work actually happening inside it.',
      fix: 'Split it before it enters the sprint. If it cannot be split, it is not understood well enough to commit to, and it belongs in refinement.',
    },
    {
      id: 'blocked-without-flag',
      arrowId: 'blocked-not-flagged',
      title: 'Blocked work that is not flagged',
      severity: 'critical',
      explanation:
        'This card sits in a Blocked column but carries no flag, so nothing about it is impeded as far as Jira is concerned. It will not appear in any blocked-work filter, no report will count it, and the moment it moves out of that column every trace that it was ever stuck disappears.',
      fix: 'Flag the card and add the reason. The flag is the data; a column is only a view of it, and a column on its own records nothing.',
    },
    {
      id: 'missing-estimate',
      arrowId: 'no-estimate',
      title: 'Committed work with no estimate',
      severity: 'major',
      explanation:
        'This card is in the sprint with no estimate at all. That makes the sprint total wrong by an unknown amount, which makes the velocity wrong, which makes every forecast built on that velocity wrong. Unlike a bad estimate, a missing one gives you nothing to correct against.',
      fix: 'Estimate it before it enters the sprint. A rough number the team argued about beats an empty field.',
    },
    {
      id: 'unassigned',
      arrowId: 'unassigned-in-sprint',
      title: 'An unassigned card in the sprint',
      severity: 'major',
      explanation:
        'The assignee slot is empty on a card the team has already committed to. It will not show up in anybody\'s "my work" view, so the most likely outcome is that nobody starts it and it is discovered on the last day of the sprint.',
      fix: 'Give it an owner at sprint planning. If nobody will take it, that is a signal the sprint is overcommitted, not a field to leave blank.',
    },
  ],
};
